import { yupResolver } from '@hookform/resolvers/yup';
import Check from '@mui/icons-material/Check';
import Close from '@mui/icons-material/Close';
import Delete from '@mui/icons-material/Delete';
import Flag from '@mui/icons-material/Flag';
import FlagOutlined from '@mui/icons-material/FlagOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { FileAttachableType, TicketState } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import ConfirmationDialog from '~components/Dialog/ConfirmationDialog';
import { Body } from '~components/Drawer/src/ui/Body';
import { Footer } from '~components/Drawer/src/ui/Footer';
import { Header } from '~components/Drawer/src/ui/Header';
import { LoadingSpinner } from '~components/Order/ordersDispatchStyledComponents';
import { useInlineUploadError } from '~hooks/useInlineUploadError/useInlineUploadError';
import { Job } from '~hooks/useJob/models';
import { Ticket } from '~hooks/useTickets/models';
import { ImageUpload, ImageUploadHandler } from '~pages/Admin/Companies/ImageUpload';
import { canEditTicket } from '~utils/tickets/ticket-utils';

import FileAttachments, {
  fileAttachmentSchema,
} from '../../../formsShared/components/FileAttachments';
import { TicketFormDTO } from '../ApprovalsComponents/ticketFormSchema';
import { Field } from './Field';
import { TicketDetailForm } from './TicketDetailForm';

interface TicketDetailsProps {
  readonly?: boolean;
  loading?: boolean;
  job: Job;
  loadId?: string;
  onApprove: (ticket: Ticket) => void;
  onChange: (ticket: TicketFormDTO) => Promise<Ticket>;
  onClose: () => void;
  onDelete?: (ticket: Ticket) => void | Promise<void>;
  onFlag: (ticke: Ticket) => Promise<Ticket>;
  onRemoveImage: (ticket: Ticket) => void | Promise<void>;
  onUploadImage: (ticket: Ticket | undefined, image: File) => Promise<void>;
  ticket?: Ticket;
}

const fileAttachmentsFormSchema = yup.object().shape({
  fileAttachments: fileAttachmentSchema,
});

interface TicketDetailsImperativeApi {
  setEditMode: (isEditing: boolean) => void;
}

export const TicketDetails = forwardRef<TicketDetailsImperativeApi, TicketDetailsProps>(
  (
    {
      loading,
      readonly,
      job,
      loadId,
      onApprove,
      onChange,
      onClose,
      onDelete,
      onFlag,
      onRemoveImage,
      onUploadImage,
      ticket,
    },
    ref,
  ) => {
    const [isDeleteImageDialogOpen, setIsDeleteImageDialogOpen] = useState(false);
    const [isDeleteTicketDialogOpen, setIsDeleteTicketDialogOpen] = useState(false);
    const imageUploadRef = useRef<ImageUploadHandler>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [isRemovingImage, setIsRemovingImage] = useState(false);
    const [isDeletingTicket, setIsDeletingTicket] = useState(false);
    const {
      uploadError: imageUploadError,
      clearUploadError: clearImageUploadError,
      showUploadError: showImageUploadError,
    } = useInlineUploadError();
    const ticketDetailFormRef = useRef<TicketDetailsImperativeApi>(null);

    useImperativeHandle(
      ref,
      () => ({
        setEditMode: (isEditing: boolean) => {
          ticketDetailFormRef.current?.setEditMode(isEditing);
        },
      }),
      [],
    );

    const form = useForm({
      resolver: yupResolver(fileAttachmentsFormSchema),
      mode: 'onSubmit',
      reValidateMode: 'onChange',
      defaultValues: {},
    });
    const {
      control,
      formState: { errors },
    } = form;

    const handleEditModeChange = useCallback((isEditing: boolean) => {
      setIsEditing(isEditing);
    }, []);

    const handleFlagTicket = useCallback(async () => {
      if (ticket) {
        try {
          setIsEditing(true);
          await onFlag(ticket);
        } finally {
          setIsEditing(false);
        }
      }
    }, [ticket, onFlag]);

    return (
      <>
        <ConfirmationDialog
          isBusy={isRemovingImage}
          isOpen={isDeleteImageDialogOpen}
          onClose={() => {
            if (!isRemovingImage) {
              setIsDeleteImageDialogOpen(false);
            }
          }}
          onConfirm={async () => {
            if (ticket) {
              try {
                setIsRemovingImage(true);
                imageUploadRef.current?.reset();
                await onRemoveImage(ticket);
                setIsDeleteImageDialogOpen(false);
              } finally {
                setIsRemovingImage(false);
              }
            }
          }}
          title={t('approvals.ticket_details.remove_image')}
        >
          <Typography>
            {t('approvals.ticket_details.remove_image_confirmation')}
          </Typography>
        </ConfirmationDialog>
        <ConfirmationDialog
          confirmButtonProps={{ children: `${t('actions.delete')}` }}
          isBusy={isDeletingTicket}
          isOpen={isDeleteTicketDialogOpen}
          onClose={() => {
            if (!isDeletingTicket) {
              setIsDeleteTicketDialogOpen(false);
            }
          }}
          onConfirm={async () => {
            if (ticket && onDelete) {
              try {
                setIsDeletingTicket(true);
                await onDelete(ticket);
                setIsDeleteTicketDialogOpen(false);
              } finally {
                setIsDeletingTicket(false);
              }
            }
          }}
          title={t('approvals.ticket_details.confirm_delete')}
        >
          <Typography>{t('approvals.ticket_details.delete_warning')}</Typography>
        </ConfirmationDialog>
        <Header
          title={
            ticket ? (
              <>
                <Typography
                  component="span"
                  variant="body1"
                  sx={{
                    fontWeight: '600',
                    mr: 2,
                  }}
                >
                  {t('approvals.ticket_details.ticket_title', {
                    number: `${ticket?.ticketNumber}`,
                  })}
                </Typography>
                <Typography
                  component="span"
                  variant="body1"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  {`${job.orderId}`}
                </Typography>
              </>
            ) : (
              <Typography
                component="span"
                variant="body1"
                sx={{
                  fontWeight: '600',
                  mr: 2,
                }}
              >
                {t('approvals.ticket_details.new_ticket')}
              </Typography>
            )
          }
        >
          <IconButton onClick={() => onClose()}>
            <Close />
          </IconButton>
        </Header>
        <Body>
          <Box
            sx={{
              display: 'flex',
              flex: 1,
            }}
          >
            <Box
              sx={{
                width: '455px',
                borderRight: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  p: 2,
                }}
              >
                <Box
                  sx={{
                    height: '36px',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 'bold',
                    }}
                  >
                    {t('approvals.ticket_details.material_ticket_upload')}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ px: 1 }}>
                <ImageUpload
                  ref={imageUploadRef}
                  isCanBeMagnified={true}
                  maxSize={10}
                  url={ticket?.imageUrl}
                  allowDelete={false}
                  disabled={
                    (ticket && !canEditTicket(ticket.state as TicketState)) || readonly
                  }
                  errorMessage={imageUploadError}
                  removeCallback={() => {
                    if (ticket) {
                      onRemoveImage(ticket);
                    }
                  }}
                  onChange={(image) => {
                    clearImageUploadError();
                    onUploadImage(ticket, image).catch((error) => {
                      // Clear the optimistic preview: allowDelete is false and the
                      // Remove button is gated on ticket.imageUrl, so a failed first
                      // upload would otherwise leave the file input unreachable and
                      // block any retry until the drawer is reopened.
                      imageUploadRef.current?.reset();
                      showImageUploadError(
                        error,
                        t('error_messages.tickets.failed_to_upload_ticket_image'),
                      );
                    });
                  }}
                  sx={{ minHeight: '450px', width: '100%' }}
                />
                {!readonly &&
                  ticket &&
                  canEditTicket(ticket.state as TicketState) &&
                  ticket.imageUrl && (
                    <Button
                      variant="outlined"
                      color="inherit"
                      startIcon={<Delete />}
                      sx={{ mt: 1 }}
                      onClick={() => {
                        setIsDeleteImageDialogOpen(true);
                      }}
                    >
                      {t('approvals.ticket_details.remove_image')}
                    </Button>
                  )}
                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Divider sx={{ mb: 2 }} />
                  {!readonly && (
                    <FileAttachments
                      fileAttachableId={loadId}
                      fileAttachableType={FileAttachableType.LOAD}
                      control={control}
                      errors={errors}
                      form={form}
                      name="fileAttachments"
                      allowPerItemUpdates
                    />
                  )}
                </Box>
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignSelf: 'flex-start',
                width: '455px',
              }}
            >
              <TicketDetailForm
                ref={ticketDetailFormRef}
                key={ticket?.id}
                readonly={readonly}
                jobId={job.id}
                orderId={job.order?.id}
                ticketCompanyId={job.company?.id}
                vetaCenterBound={job.vetaCenterBound}
                onChange={onChange}
                onDelete={() => {
                  setIsDeleteTicketDialogOpen(true);
                }}
                onEditModeChange={handleEditModeChange}
                ticket={ticket}
              />

              <Header
                title={
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 'bold',
                    }}
                  >
                    {t('approvals.ticket_details.load_details')}
                  </Typography>
                }
              />

              <Field label={t('common.project')} value={job.projectName} />
              <Field label={t('common.order')} value={job.orderId} />
              <Field label={t('form_fields.job')} value={job.jobId} />
              <Field label={t('common.customer')} value={job.order?.account?.name} />
              <Field
                label={t('form_fields.drop_off')}
                value={job.dropOffWayPoints[0]?.site?.name}
              />
              <Field
                label={t('common.vendor')}
                value={job.vendorJobAssignment?.vendorAccount?.name}
              />
              <Field label={t('form_fields.driver')} value={job.driver?.fullName} />
              <Field label={t('form_fields.equipment')} value={job.equipment?.name} />
            </Box>
            {loading && (
              <Box
                sx={(theme) => ({
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor:
                    theme.palette.mode === 'dark'
                      ? 'rgba(10, 24, 32, 0.7)'
                      : 'rgba(255, 255, 255, 0.7)',
                })}
              >
                <LoadingSpinner isVisible={true} />
              </Box>
            )}
          </Box>
        </Body>
        <Footer>
          {ticket && !readonly && canEditTicket(ticket.state as TicketState) && (
            <>
              <Button
                color="inherit"
                disabled={isEditing || loading}
                onClick={handleFlagTicket}
                startIcon={ticket.flagged ? <Flag /> : <FlagOutlined />}
                variant="outlined"
              >
                {ticket.flagged ? t('approvals.remove_flag') : t('approvals.add_flag')}
              </Button>
              <Button
                color="success"
                disabled={isEditing || loading}
                onClick={() => onApprove(ticket)}
                startIcon={<Check />}
                variant="contained"
              >
                {t('approvals.approve')}
              </Button>
            </>
          )}
        </Footer>
      </>
    );
  },
);

TicketDetails.displayName = 'TicketDetails';
